import Color from '@tiptap/extension-color'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'
import { TextStyle } from '@tiptap/extension-text-style'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { useEffect, useMemo, useRef } from 'react'

import { cn } from '@/lib/utils'

import { useImagePicker } from './ImagePickerContext'
import { ButtonExtension } from './extensions/button'
import { ImageExtension } from './extensions/image'
import {
    ImagePickerStorageExtension,
    getImagePickerStorage,
} from './extensions/image-picker-storage'
import { SlashCommandExtension } from './extensions/slash-command'
import { SpacerExtension } from './extensions/spacer'
import { VariableExtension } from './extensions/variable'
import { EmailBubbleMenu } from './menus/BubbleMenu'
import { renderEmailDocument } from './renderer/render'
import type { EmailBuilderApi, EmailBuilderProps } from './types'

import './email-builder.css'

/**
 * <EmailBuilder /> — the merchant-facing Tiptap 3 email editor.
 *
 * Key traits (vs the legacy <Textarea>):
 *   - `/` slash menu to insert any block (Maily-inspired, tagada-tailored)
 *   - `@` variable picker sourced from the template's typed variable contract
 *   - Bubble menu for inline formatting (bold/italic/link/variable)
 *   - Email-safe button block with radius/variant/alignment/color controls
 *   - Spacer and divider blocks
 *   - Outputs clean email-safe HTML via `renderEmailDocument()`
 *
 * What we deliberately skipped for v1 (and why):
 *   - Columns / sections: most tagada templates already provide the outer
 *     shell via React Email components, so columns inside the body create
 *     more confusion than value for beginner merchants.
 *   - Repeat blocks: our emails use fixed order-summary components, not
 *     editor-authored lists.
 *   - Custom product / promo / order-summary blocks: Phase 2 — the
 *     extension surface is already in place (see ButtonExtension for the
 *     pattern).
 */
export function EmailBuilder(props: EmailBuilderProps) {
    const {
        value,
        onChange,
        variables,
        placeholder = "Type '/' for blocks, '@' for variables",
        readOnly,
        className,
        contentClassName,
        onReady,
    } = props

    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange

    const { openImagePicker } = useImagePicker()

    const extensions = useMemo(
        () => [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
                codeBlock: false,
                link: {
                    openOnClick: false,
                    HTMLAttributes: {
                        rel: 'noopener noreferrer',
                        target: '_blank',
                    },
                },
            }),
            TextStyle,
            Color,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
                defaultAlignment: 'left',
            }),
            ImageExtension.configure({
                HTMLAttributes: { class: 'email-builder-image' },
                allowBase64: true,
            }),
            ImagePickerStorageExtension,
            Placeholder.configure({
                placeholder: ({ node }) => {
                    if (node.type.name === 'heading') return 'Heading'
                    return placeholder
                },
                emptyEditorClass: 'is-editor-empty',
                includeChildren: false,
            }),
            VariableExtension.configure({ variables }),
            ButtonExtension,
            SpacerExtension,
            SlashCommandExtension,
        ],
        [placeholder, variables],
    )

    const editor = useEditor(
        {
            extensions,
            content: value || { type: 'doc', content: [{ type: 'paragraph' }] },
            editable: !readOnly,
            // Avoids "flushSync was called from inside a lifecycle method"
            // when the editor renders during the parent's render pass.
            immediatelyRender: false,
            editorProps: {
                attributes: {
                    class: cn(
                        'email-builder-content focus:outline-none',
                        contentClassName,
                    ),
                },
            },
            onUpdate: ({ editor }) => {
                onChangeRef.current(editor.getJSON())
            },
        },
        // Extensions are memoized above, so recreate editor only if they change
        [extensions],
    )

    // Bridge React-side image picker into editor.storage so any extension
    // (slash command, image node view, future blocks) can summon it.
    useEffect(() => {
        const storage = getImagePickerStorage(editor)
        if (!storage) return
        storage.openPicker = openImagePicker
        return () => {
            storage.openPicker = null
        }
    }, [editor, openImagePicker])

    // Sync external value changes (e.g. user picks a starter template)
    const lastPushedRef = useRef<string>('')
    useEffect(() => {
        if (!editor) return
        const serialized = JSON.stringify(value)
        if (serialized === lastPushedRef.current) return
        const currentSerialized = JSON.stringify(editor.getJSON())
        if (serialized === currentSerialized) return
        lastPushedRef.current = serialized
        if (value) {
            editor.commands.setContent(value, { emitUpdate: false })
        }
    }, [editor, value])

    // Expose imperative handle for consumers that want to drive the editor
    useEffect(() => {
        if (!editor || !onReady) return
        const api: EmailBuilderApi = {
            setDocument: (doc) =>
                editor.commands.setContent(doc, { emitUpdate: true }),
            focus: () => editor.commands.focus(),
            getHtml: () => renderEmailDocument(editor.getJSON()),
            getJson: () => editor.getJSON(),
        }
        onReady(api)
    }, [editor, onReady])

    return (
        <div
            className={cn(
                'email-builder flex flex-col rounded-md border bg-background',
                className,
            )}
        >
            <EmailBubbleMenu editor={editor} variables={variables} />
            <EditorContent
                editor={editor}
                className="min-h-[400px] px-6 py-5 text-[15px]"
            />
            <StatusBar editor={editor} />
        </div>
    )
}

function StatusBar({ editor }: { editor: ReturnType<typeof useEditor> | null }) {
    if (!editor) return null
    return (
        <div className="flex items-center justify-between border-t bg-muted/20 px-3 py-1.5 text-[11px] text-muted-foreground">
            <span>
                <kbd className="rounded border bg-background px-1">/</kbd> blocks ·{' '}
                <kbd className="rounded border bg-background px-1">@</kbd> variables ·{' '}
                <kbd className="rounded border bg-background px-1">⌘K</kbd> link
            </span>
            <span>{editor.getText().length} chars</span>
        </div>
    )
}
